/**
 * Foto principal de la ficha, con miniaturas cuando el producto tiene mas de
 * una (ADM-04).
 *
 * La principal es la imagen mas grande de la vista y por lo tanto el LCP: se
 * carga sin diferir, sin importar cual de las fotos este seleccionada. Elegir
 * una miniatura solo cambia cual imagen ya cargada se ve grande — no hay
 * pedido de red de por medio, las fotos ya vinieron todas en `producto.imagenes`.
 *
 * Con el mouse encima, la foto se ve ampliada en un panel al costado
 * (`useZoomFoto`), que reemplaza al leve agrandado que tenia antes.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { ImagenProducto } from '@elcarpintero/compartido';

import { useZoomFoto } from '../../hooks/use-zoom-foto';
import { FotoProducto, Imagen } from '../comunes/Imagen';

/**
 * La foto ocupa la mitad de la ficha en escritorio y el ancho entero bajo
 * 1080 px (`ficha.css`). La lupa usa el original, no la copia.
 */
const TAMANOS_FOTO = '(max-width:1080px) 100vw, 620px';
/** Las miniaturas son de 64 px (`ficha.css`). */
const TAMANOS_MINIATURA = '64px';

export interface GaleriaProductoProps {
  imagenes: ImagenProducto[];
  nombre: string;
  /** Etiquetas que van sobre la foto: oferta, sin stock. */
  etiquetas?: ReactNode;
}

export function GaleriaProducto({ imagenes, nombre, etiquetas }: GaleriaProductoProps) {
  const [indice, setIndice] = useState(0);
  const principal = imagenes[indice] ?? imagenes[0] ?? null;

  const variasFotos = imagenes.length > 1;
  const { zoom, mover, salir } = useZoomFoto();

  return (
    <div className="ficha-galeria">
      {variasFotos ? (
        <nav className="ficha-rail-miniaturas" aria-label={`Fotos de ${nombre}`}>
          <ul className="ficha-miniaturas">
            {imagenes.map((imagen, posicion) => (
              <li key={imagen.id}>
                <button
                  type="button"
                  className={posicion === indice ? 'ficha-miniatura activa' : 'ficha-miniatura'}
                  aria-pressed={posicion === indice}
                  aria-label={`Ver foto ${posicion + 1} de ${imagenes.length}`}
                  onClick={() => {
                    setIndice(posicion);
                  }}
                >
                  <Imagen
                    src={imagen.url}
                    alt=""
                    ancho={imagen.ancho}
                    alto={imagen.alto}
                    variantes={imagen.variantes}
                    tamanos={TAMANOS_MINIATURA}
                  />
                </button>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}

      <div className="ficha-foto-envoltura">
        <figure
          className="ficha-foto"
          onPointerEnter={mover}
          onPointerMove={mover}
          onPointerLeave={salir}
        >
          {etiquetas !== undefined ? <p className="ficha-etqs">{etiquetas}</p> : null}
          <FotoProducto imagen={principal} nombre={nombre} prioridad tamanos={TAMANOS_FOTO} />
          {variasFotos ? (
            <span className="ficha-posicion" aria-hidden="true">
              {indice + 1} / {imagenes.length}
            </span>
          ) : null}
          {zoom !== null ? (
            <span
              className="ficha-lente"
              aria-hidden="true"
              style={{
                left: zoom.lente.x,
                top: zoom.lente.y,
                width: zoom.lente.ancho,
                height: zoom.lente.alto,
              }}
            />
          ) : null}
        </figure>
        {/* La foto ampliada es decorativa: repite la que ya tiene su alt, asi
            que va como fondo y fuera del arbol de accesibilidad. */}
        {zoom !== null && principal !== null ? (
          <div
            className="ficha-zoom"
            aria-hidden="true"
            style={{
              backgroundImage: `url("${principal.url}")`,
              backgroundSize: `${zoom.fondo.ancho}px ${zoom.fondo.alto}px`,
              backgroundPosition: `${zoom.fondo.x}px ${zoom.fondo.y}px`,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
